// PrintPageEditor.jsx
import "./print-page-editor.scss";
import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SocketContext } from "../../app/socket";
import EditorWrapper from "./EditorWrapper/editorWrapper";
import EditorPreview from "./EditorPreview/editorPreview";
import { DragDropContext } from "react-beautiful-dnd";

function reorderList(list, startIndex, endIndex) {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function moveBetweenLists(sourceList, destList, sourceIndex, destIndex) {
  const newSource = [...sourceList];
  const newDest = [...destList];
  const [removed] = newSource.splice(sourceIndex, 1);
  newDest.splice(destIndex, 0, removed);
  return { newSource, newDest };
}

function gatherAllDroppableIds(editorData) {
  const result = new Set();
  result.add("all-sections");

  editorData.sections.forEach((section) => {
    result.add(`components-section-${section.id}`);

    // Add nested section columns
    section.components.forEach((component) => {
      if (component.type === "nestedSection") {
        component.columns.forEach((col) => {
          result.add(`nestedSection-col-${component.id}__${col.id}`);
        });
      }
    });
  });

  return result;
}

function buildSection() {
  return {
    id: "section-" + Date.now(),
    type: "section",
    name: "New Section",
    layout: "single-column",
    styles: {
      padding: "1em",
      border: "1px dotted #ccc",
      marginBottom: "1em",
      display: "flex",
      flexDirection: "column",
    },
    components: [],
  };
}

/**
 * Build a component by toolboxId (tb-text, tb-image, tb-nestedSection).
 */
function buildComponent(toolboxId) {
  switch (toolboxId) {
    case "tb-text":
      return {
        id: "comp-" + Date.now(),
        type: "text",
        content: "New text",
        styles: { fontSize: "16px" },
      };
    case "tb-image":
      return {
        id: "comp-" + Date.now(),
        type: "image",
        content: "/placeholder.jpg",
        styles: { container: { width: "200px" } },
      };
    case "tb-gridSection":
      const now = Date.now();
      return {
        id: `comp-${now}`,
        type: "gridSection",
        columns: [
          {
            id: `col-${now}-1`,
            width: "50%",
            components: [],
          },
          {
            id: `col-${now}-2`,
            width: "50%",
            components: [],
          },
        ],
      };
    default:
      return {
        id: "comp-" + Date.now(),
        type: "text",
        content: "Unknown",
        styles: {},
      };
  }
}

/** Find a top-level section by ID. */
function findSectionById(list, sectionId) {
  return list.find((s) => String(s.id) === String(sectionId)) || null;
}

function PrintPageEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useContext(SocketContext).socket;

  const [template, setTemplate] = useState(null);
  const [printTemplatesId, setPrintTemplatesId] = useState(null);
  const [editorData, setEditorData] = useState(null);

  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedComponentId, setSelectedComponentId] = useState(null);

  const [dragKey, setDragKey] = useState(0);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const tid = p.get("template-id");
    if (!tid) {
      navigate("/print");
    } else {
      setPrintTemplatesId(tid);
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (printTemplatesId) getTemplate();
    // Cleanup
    return () => {
      if (socket && printTemplatesId) {
        socket.off("printTemplates" + printTemplatesId);
        socket.off("refreshPrintTemplates");
      }
    };
  }, [printTemplatesId, socket]);

  function getTemplate() {
    const args = { print_templates_id: printTemplatesId };
    socket.on("printTemplates" + printTemplatesId, (data) => {
      if (data && data[0]) setTemplate(data[0]);
      else setTemplate(null);
    });
    socket.emit("getPrintTemplates", args);
    socket.on("refreshPrintTemplates", () => {
      socket.emit("getPrintTemplates", args);
    });
  }

  useEffect(() => {
    if (template?.template_data) {
      try {
        setEditorData(JSON.parse(template.template_data));
      } catch {
        setEditorData(null);
      }
    } else {
      setEditorData(null);
    }
  }, [template]);

  const droppablesRef = useRef(new Set());

  useEffect(() => {
    if (!editorData) return;

    // Gather current droppable IDs
    const currentDroppables = gatherAllDroppableIds(editorData);

    // Compare to previous
    let hasNew = false;
    for (let id of currentDroppables) {
      if (!droppablesRef.current.has(id)) {
        hasNew = true;
        break;
      }
    }
    // If there's a brand-new droppable => re-mount once
    if (hasNew) {
      setDragKey((k) => k + 1);
      droppablesRef.current = currentDroppables; // update the ref
    } else {
      // If we removed droppables, you might do the same for removal, or ignore if it doesn't matter
      droppablesRef.current = currentDroppables;
    }
  }, [editorData]);

  function handleSelect(sectionId, componentId) {
    setSelectedSectionId(sectionId);
    setSelectedComponentId(componentId);
  }

  function clearSelection() {
    setSelectedSectionId(null);
    setSelectedComponentId(null);
  }

  function updateComponent(sectionId, componentId, changes) {
    setEditorData((prev) => {
      if (!prev?.sections) return prev;
      const newData = structuredClone(prev);
      const sec = findSectionById(newData.sections, sectionId);
      if (!sec) return prev;

      sec.components = sec.components.map((c) => {
        if (String(c.id) === String(componentId)) {
          return { ...c, ...changes };
        }
        return c;
      });
      return newData;
    });
  }

  function updateSection(sectionId, changes) {
    setEditorData((prev) => {
      if (!prev?.sections) return prev;
      const newData = structuredClone(prev);

      const sec = findSectionById(newData.sections, sectionId);
      if (!sec) return prev;

      Object.assign(sec, changes);
      return newData;
    });
  }

  function parseGridDroppableId(droppableId) {
    // droppableId = "gridSection-col-<gridSectionId>__<colId>"
    const [prefix, colId] = droppableId.split("__");
    // prefix should be "gridSection-col-<gridSectionId>"
    const gridSectionId = prefix.replace("gridSection-col-", "");
    return [gridSectionId, colId];
  }

  function findGridSection(sections, gridSectionId) {
    for (const sec of sections) {
      for (const comp of sec.components) {
        if (
          comp.type === "gridSection" &&
          String(comp.id) === String(gridSectionId)
        ) {
          return comp; // or if you need the section too, return { sectionObj: sec, gridComp: comp }
        }
      }
    }
    return null;
  }

  function handleDragEnd(result) {
    if (!result.destination) return;

    const { source, destination, draggableId, type } = result;
    const fromId = source.droppableId;
    const toId = destination.droppableId;
    const fromIndex = source.index;
    const toIndex = destination.index;

    setEditorData((prev) => {
      if (!prev?.sections) return prev;
      const newData = structuredClone(prev);

      // ─────────────────────────────────────────────────────
      // 1) DRAG a new SECTION from toolbox => top-level only
      // ─────────────────────────────────────────────────────
      if (type === "SECTION" && fromId === "toolbox-sections") {
        if (toId === "all-sections") {
          const newSec = buildSection(); // your buildSection() for top-level
          newData.sections.splice(toIndex, 0, newSec);
        }
        return newData;
      }

      // ─────────────────────────────────────────────────────
      // 2) DRAG a new COMPONENT (from "toolbox-components")
      // ─────────────────────────────────────────────────────
      if (type === "COMPONENT" && fromId === "toolbox-components") {
        // If user drops onto top-level "all-sections", ignore (or handle differently)
        if (toId === "all-sections") return prev;

        // If user drops onto a normal section's components
        if (toId.startsWith("components-section-")) {
          const secId = toId.replace("components-section-", "");
          const sec = findSectionById(newData.sections, secId);
          if (!sec) return prev;

          const newComp = buildComponent(draggableId);
          sec.components.splice(toIndex, 0, newComp);
          return newData;
        }

        // If user drops into a gridSection column
        if (toId.startsWith("gridSection-col-")) {
          const [gridSecId, colId] = parseGridDroppableId(toId);
          const gridComp = findGridSection(newData.sections, gridSecId);
          if (!gridComp) return prev;

          const column = gridComp.columns.find((c) => c.id === colId);
          if (!column) return prev;

          const newComp = buildComponent(draggableId);
          column.components.splice(toIndex, 0, newComp);
          return newData;
        }

        // If none matched, do nothing
        return prev;
      }

      // ─────────────────────────────────────────────────────
      // 3) REORDER / MOVE EXISTING SECTION => top-level only
      // ─────────────────────────────────────────────────────
      if (type === "SECTION") {
        // from "all-sections" => to "all-sections"
        if (fromId === "all-sections" && toId === "all-sections") {
          const reordered = reorderList(newData.sections, fromIndex, toIndex);
          newData.sections = reordered;
        }
        return newData;
      }

      // ─────────────────────────────────────────────────────
      // 4) REORDER / MOVE an EXISTING COMPONENT
      // ─────────────────────────────────────────────────────
      if (type === "COMPONENT") {
        // (a) from a normal section
        if (fromId.startsWith("components-section-")) {
          const fromSecId = fromId.replace("components-section-", "");
          const fromSec = findSectionById(newData.sections, fromSecId);
          if (!fromSec) return prev;

          const item = fromSec.components[fromIndex];
          if (!item) return prev;

          // same droppable => reorder
          if (fromId === toId) {
            const reordered = reorderList(
              fromSec.components,
              fromIndex,
              toIndex
            );
            fromSec.components = reordered;
            return newData;
          }

          // move to another normal section
          if (toId.startsWith("components-section-")) {
            const toSecId = toId.replace("components-section-", "");
            const toSec = findSectionById(newData.sections, toSecId);
            if (!toSec) return prev;

            const { newSource, newDest } = moveBetweenLists(
              fromSec.components,
              toSec.components,
              fromIndex,
              toIndex
            );
            fromSec.components = newSource;
            toSec.components = newDest;
            return newData;
          }

          // move to a gridSection column
          if (toId.startsWith("gridSection-col-")) {
            const [gridSecId, colId] = parseGridDroppableId(toId);
            const gridComp = findGridSection(newData.sections, gridSecId);
            if (!gridComp) return prev;

            const column = gridComp.columns.find((c) => c.id === colId);
            if (!column) return prev;

            // remove from old
            fromSec.components.splice(fromIndex, 1);
            // insert in new
            column.components.splice(toIndex, 0, item);
            return newData;
          }

          return newData;
        }

        // (b) from a gridSection column
        if (fromId.startsWith("gridSection-col-")) {
          const [fromGridId, fromColId] = parseGridDroppableId(fromId);
          const fromGridComp = findGridSection(newData.sections, fromGridId);
          if (!fromGridComp) return prev;

          const fromColumn = fromGridComp.columns.find(
            (c) => c.id === fromColId
          );
          if (!fromColumn) return prev;

          const item = fromColumn.components[fromIndex];
          if (!item) return prev;

          // reorder in the same column
          if (fromId === toId) {
            const reordered = reorderList(
              fromColumn.components,
              fromIndex,
              toIndex
            );
            fromColumn.components = reordered;
            return newData;
          }

          // move to another gridSection column
          if (toId.startsWith("gridSection-col-")) {
            const [toGridId, toColId] = parseGridDroppableId(toId);
            const toGridComp = findGridSection(newData.sections, toGridId);
            if (!toGridComp) return prev;

            const toColumn = toGridComp.columns.find((c) => c.id === toColId);
            if (!toColumn) return prev;

            // remove from old
            fromColumn.components.splice(fromIndex, 1);
            // add to new
            toColumn.components.splice(toIndex, 0, item);
            return newData;
          }

          // move to a normal section
          if (toId.startsWith("components-section-")) {
            const toSecId = toId.replace("components-section-", "");
            const toSec = findSectionById(newData.sections, toSecId);
            if (!toSec) return prev;

            // remove from old column
            fromColumn.components.splice(fromIndex, 1);
            // add to new section
            toSec.components.splice(toIndex, 0, item);
            return newData;
          }

          return newData;
        }

        // fallback
        return prev;
      }

      // If it wasn't SECTION or COMPONENT, or we couldn't handle it
      return prev;
    });
  }

  return (
    <DragDropContext key={dragKey} onDragEnd={handleDragEnd}>
      <div className="print-page-editor" style={{ overflow: "visible" }}>
        {editorData && (
          <EditorWrapper
            editorData={editorData}
            selectedSectionId={selectedSectionId}
            selectedComponentId={selectedComponentId}
            updateComponent={updateComponent}
            updateSection={updateSection}
            clearSelection={clearSelection}
          />
        )}
        <div className="editor-preview">
          <div className="editor-preview__wrapper">
            {editorData && (
              <EditorPreview
                data={editorData}
                onSelect={handleSelect}
                setEditorData={setEditorData}
              />
            )}
          </div>
        </div>
      </div>
    </DragDropContext>
  );
}

export default PrintPageEditor;
